import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@apollo/client';
import { SelectOptionType } from '../../../../../../../components/dialog/helper';
import { useInstallationContext } from '../../../../../../../context/installation';
import {
  MeasurementChannelKind,
  OnMeasurementSubscription,
  OnMeasurementSubscriptionVariables,
  ScalarParameterTypeInternal,
  SubPriority,
  VectorParameterTypeInternal,
} from '../../../../../../../data-access/gql-types/graphql';
import { ON_MEASUREMENT } from '../../../../../../../data-access/subscriptions/on-measurement';
import { useMeasurementMapOperations } from '../../../../../hooks/measurement-map/use-measurement-map-operations';
import { ChannelMeterType, ChannelType } from '../../../../../types';

const availableParameters = [
  VectorParameterTypeInternal.ActivePower,
  VectorParameterTypeInternal.Current,
  VectorParameterTypeInternal.Voltage,
];

const currentVectorsAvailable = [
  VectorParameterTypeInternal.ActivePower,
  VectorParameterTypeInternal.Current,
  VectorParameterTypeInternal.Voltage,
  VectorParameterTypeInternal.ForwardActiveEnergy,
];

const currentScalarsAvailable = [
  ScalarParameterTypeInternal.AverageActivePower,
  ScalarParameterTypeInternal.AverageCurrent,
  ScalarParameterTypeInternal.AverageVoltage,
  ScalarParameterTypeInternal.MaxActivePower,
  ScalarParameterTypeInternal.MaxCurrent,
  ScalarParameterTypeInternal.MaxVoltage,
  ScalarParameterTypeInternal.MinActivePower,
  ScalarParameterTypeInternal.MinCurrent,
  ScalarParameterTypeInternal.MinVoltage,
  ScalarParameterTypeInternal.EcoIndicator,
];

export const useMeasurements = (channel: ChannelType) => {
  const { t } = useTranslation('channel-details');
  const { skipLavvaFetch } = useInstallationContext();
  const { measurementMap } = useMeasurementMapOperations();
  const supportedVectors = (channel.data as ChannelMeterType).supportedVectorParameters;
  const supportedScalars = (channel.data as ChannelMeterType).supportedScalarParameters;

  const vectorParameterTypes = currentVectorsAvailable.flatMap((param) => {
    const value = measurementMap?.meter.vectors.find((x) => x.type === param);
    return value !== undefined ? [value] : [];
  });

  const scalarParameterTypes = currentScalarsAvailable.flatMap((param) => {
    const value = measurementMap?.meter.scalars.find((x) => x.type === param);
    return value !== undefined ? [value] : [];
  });

  const inputVectorParameters = vectorParameterTypes.filter((x) => supportedVectors.includes(x.value));
  const inputScalarParameters = scalarParameterTypes.filter((x) => supportedScalars.includes(x.value));

  const { data, loading } = useSubscription<OnMeasurementSubscription, OnMeasurementSubscriptionVariables>(
    ON_MEASUREMENT,
    {
      variables: {
        input: {
          channelId: channel.id,
          kind: MeasurementChannelKind.Meter,
          priority: SubPriority.High,
          scalarParameterTypes: inputScalarParameters.map((x) => x.value),
          vectorParameterTypes: inputVectorParameters.map((x) => x.value),
          indices: [],
        },
      },
      skip: skipLavvaFetch || ![...inputVectorParameters, inputScalarParameters].length,
    },
  );

  const parameters: SelectOptionType<VectorParameterTypeInternal>[] = useMemo(
    () =>
      availableParameters
        .filter((x) => {
          if (!inputVectorParameters.length && x === VectorParameterTypeInternal.ActivePower) return true;
          return inputVectorParameters.find((v) => v.type === x);
        })
        .map((x, index) => ({
          label: t(`energyConsumption.${x}`),
          value: x,
          weight: index,
          enabled: true,
        })),
    [inputVectorParameters, availableParameters, t],
  );

  const vectorMeasurements = useMemo(() => {
    if (data?.onMeasurement) {
      if ('vectorMeasurements' in data.onMeasurement.data) {
        return data.onMeasurement.data.vectorMeasurements;
      }
    }

    return null;
  }, [data?.onMeasurement]);

  const scalarMeasurements = useMemo(() => {
    if (data?.onMeasurement) {
      if ('scalarMeasurements' in data.onMeasurement.data) {
        return data.onMeasurement.data.scalarMeasurements;
      }
    }

    return null;
  }, [data?.onMeasurement]);

  return {
    parameters,
    data: data?.onMeasurement.data,
    loading,
    vectorMeasurements,
    scalarMeasurements,
  };
};
